import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Input, notification, Spin, Pagination, InputNumber, Checkbox } from 'antd';
import { Button as CrceButton, ConfirmationModal } from '@crc-evans/ui-components';
import { Loader2, Plus, Save, Search, Trash2, Users } from 'lucide-react';
import moment from 'moment';
import { connect } from 'react-redux';
import get from 'lodash/get';
import {
  fetchTechnicianLog,
  updateTechnicianLog,
  updateDsrLogSections,
} from '../../redux/dsr/action';
import { addRole, fetchRoles } from '../../redux/roles/action';
import {
  buildDsrTechnicianRoleOptions,
  getDsrTechnicianAllowedRoles,
} from '../../constants/dprPersonnelRoleGroups';
import TechnicianLogCustomSelect from './TechnicianLogCustomSelect';
import AddTechnicianModal from './AddTechnicianModal';
import {
  buildTechnicianRowFromTeamMember,
  fetchProjectTeamMembers,
  isPendingTechnicianRowId,
  setTeamMemberDsrListing,
} from '../../services/dsrTechnicianTeam';
import './TechnicianLogSection.css';

const { TextArea } = Input;

const STATUS_SELECT_OPTIONS = [
  'Select Status',
  'Mainline',
  'Offline',
  'Offshore',
  'Tie-In',
  'Standby',
  'Mobilize',
  'De-Mobilize',
];

const TechnicianLogSection = ({
  shift,
  selectedDate,
  selectedProject,
  selectedMainline,
  currentDsrLog,
  isSkipped,
  isSubmitted,
  onComplete,
  onCancel,
  isShiftSkipped = false,
  onSkipChange,
  onFetchTechnicianLog,
  onUpdateTechnicianLog,
  onUpdateDsrLogSections,
  isTechnicianLogLoading,
  isUpdatingTechnicianLog,
  currentTechnicianLog,
  globalRoles,
  onAddRole,
  onFetchRoles,
}) => {
  const [rowData, setRowData] = useState([]);
  const [comments, setComments] = useState('');
  const [skipped, setSkipped] = useState(false);
  const [paginationState, setPaginationState] = useState({ current: 1, pageSize: 10 });
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [projectTeamMembers, setProjectTeamMembers] = useState([]);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isAddingTechnicians, setIsAddingTechnicians] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [nameSearch, setNameSearch] = useState('');

  const normEmail = (value) => String(value ?? '').trim().toLowerCase();

  const getShiftNumber = (shiftStr) => {
    return shiftStr === 'day' ? 0 : 1;
  };

  const convertApiDataToTableFormat = (apiData) => {
    if (!apiData || !apiData.dsrLogTechnicianMetadata) return [];

    const convertedData = apiData.dsrLogTechnicianMetadata.map((item) => ({
      id: item.id,
      name: `${item.userFirstName} ${item.userLastName}`,
      status: getStatusFromApiData(item),
      roles: (typeof item.role === 'string' && item.role.trim().length > 0) ? item.role : 'Select Role',
      hrs: item.hrs || 0,
      overTime: item.overTime || 0,
      apiData: item,
    }));

    return convertedData;
  };

  const getStatusFromApiData = (item) => {
    const allowedStatuses = new Set([
      'Mainline',
      'Offline',
      'Offshore',
      'Tie-In',
      'Standby',
      'Mobilize',
      'De-Mobilize',
    ]);

    if (typeof item.status === 'string' && item.status.length > 0) {
      const incoming = item.status.trim();
      for (const s of allowedStatuses) {
        if (s.toLowerCase() === incoming.toLowerCase()) return s;
      }
    }

    if (item.mobilize === 1) return 'Mobilize';
    if (item.deMobilize === 1) return 'De-Mobilize';

    if (
      item.mobilize === 0 &&
      item.onSite === 0 &&
      item.onLeave === 0 &&
      item.onVisit === 0 &&
      item.onTravel === 0 &&
      item.deMobilize === 0
    ) {
      return 'Select Status';
    }

    return 'Select Status';
  };

  const mapUiStatusToApi = (status) => {
    const allowed = new Set([
      'Mainline',
      'Offline',
      'Offshore',
      'Tie-In',
      'Standby',
      'Mobilize',
      'De-Mobilize',
    ]);
    if (allowed.has(status)) return status;
    return '';
  };

  const projectId = selectedProject?.id;
  const mainlineId = selectedMainline?.id;
  const logDateKey = useMemo(
    () => (selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : ''),
    [selectedDate],
  );

  // Fetch technician log when component mounts or dependencies change (use primitive deps to avoid duplicate GETs when parent passes new object references).
  useEffect(() => {
    if (!projectId || !mainlineId || !logDateKey || isSkipped) {
      return;
    }
    let userEmail = '';
    try {
      userEmail = JSON.parse(localStorage.getItem('user') || '{}').email || '';
    } catch (_) {
      return;
    }
    if (!userEmail) return;

    const shiftNumber = getShiftNumber(shift);
    onFetchTechnicianLog({
      projectId,
      logDate: logDateKey,
      userEmail,
      shift: shiftNumber,
      sowId: mainlineId,
    });
  }, [projectId, mainlineId, logDateKey, shift, isSkipped, onFetchTechnicianLog]);

  useEffect(() => {
    if (!projectId || isSkipped) {
      setProjectTeamMembers([]);
      return;
    }
    let cancelled = false;
    fetchProjectTeamMembers(projectId)
      .then((members) => {
        if (!cancelled) setProjectTeamMembers(members);
      })
      .catch(() => {
        if (!cancelled) setProjectTeamMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, isSkipped]);

  useEffect(() => {
    if (currentTechnicianLog) {
      const convertedData = convertApiDataToTableFormat(currentTechnicianLog);
      setRowData(convertedData);
      setComments(currentTechnicianLog.issues || '');
      setSelectedRowKeys([]);
      setNameSearch('');
    }
  }, [currentTechnicianLog]);

  useEffect(() => {
    setSkipped(isSkipped || false);
  }, [isSkipped, shift, selectedDate]);

  const updateRowField = useCallback((id, field, value) => {
    setRowData((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  }, []);

  const filteredRows = useMemo(() => {
    const q = nameSearch.trim().toLowerCase();
    if (!q) return rowData;
    return rowData.filter((row) => {
      const haystack = [
        row.name,
        row.apiData?.userFirstName,
        row.apiData?.userLastName,
        row.apiData?.userEmail,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [rowData, nameSearch]);

  const totalRows = filteredRows.length;
  const { current: currentPage, pageSize } = paginationState;
  const maxPage = Math.max(1, totalRows === 0 ? 1 : Math.ceil(totalRows / pageSize));
  const page = Math.min(currentPage, maxPage);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  const existingTechnicianEmails = useMemo(
    () => rowData.map((row) => row.apiData?.userEmail).filter(Boolean),
    [rowData],
  );

  const pagedRowIds = useMemo(() => pagedRows.map((r) => r.id), [pagedRows]);
  const allInPageSelected = pagedRowIds.length > 0
    && pagedRowIds.every((id) => selectedRowKeys.includes(id));
  const someInPageSelected = pagedRowIds.some((id) => selectedRowKeys.includes(id))
    && !allInPageSelected;

  const toggleSelectAllInPage = () => {
    if (allInPageSelected) {
      setSelectedRowKeys((prev) => prev.filter((id) => !pagedRowIds.includes(id)));
    } else {
      setSelectedRowKeys((prev) => [...new Set([...prev, ...pagedRowIds])]);
    }
  };

  const handleAddTechnicians = async (members) => {
    if (!members?.length) return;
    setIsAddingTechnicians(true);
    try {
      const additions = members.map(buildTechnicianRowFromTeamMember);
      setRowData((prev) => {
        const emails = new Set(prev.map((r) => normEmail(r.apiData?.userEmail)));
        const next = [...prev];
        additions.forEach((row) => {
          const email = normEmail(row.apiData?.userEmail);
          if (email && emails.has(email)) return;
          if (email) emails.add(email);
          next.push(row);
        });
        return next;
      });
      setIsAddModalVisible(false);
      notification.success({
        message: `${members.length} technician${members.length === 1 ? '' : 's'} added.`,
        description: 'Save & Next to persist. Team members not listed in DSR will be updated on save.',
      });
    } finally {
      setIsAddingTechnicians(false);
    }
  };

  const handleDeleteSelected = () => {
    if (!selectedRowKeys.length) return;
    setIsDeleteConfirmOpen(true);
  };

  const confirmDeleteSelected = () => {
    setRowData((prev) => prev.filter((row) => !selectedRowKeys.includes(row.id)));
    setSelectedRowKeys([]);
    setIsDeleteConfirmOpen(false);
  };

  const ensureDsrListingForNewRows = async () => {
    const newRows = rowData.filter((row) => row._isNew && row._teamMember);
    const updates = newRows.filter((row) => !row._teamMember?.accessToDsr);
    if (!updates.length || !projectId) return;

    await Promise.all(
      updates.map((row) => setTeamMemberDsrListing(row._teamMember, projectId, true)),
    );
  };

  useEffect(() => {
    if (currentPage > maxPage) {
      setPaginationState((s) => ({ ...s, current: maxPage }));
    }
  }, [currentPage, maxPage]);

  useEffect(() => {
    setPaginationState((s) => ({ ...s, current: 1 }));
  }, [nameSearch]);

  const roleOptions = useMemo(() => {
    const collected = [];

    if (Array.isArray(currentTechnicianLog?.roles) && currentTechnicianLog.roles.length > 0) {
      collected.push(...currentTechnicianLog.roles.map((item) => item?.role).filter(Boolean));
    }

    if (Array.isArray(currentTechnicianLog?.dsrLogTechnicianMetadata)) {
      collected.push(
        ...currentTechnicianLog.dsrLogTechnicianMetadata
          .map((item) => item?.role)
          .filter((role) => typeof role === 'string' && role.trim().length > 0)
      );
    }

    if (Array.isArray(globalRoles)) {
      collected.push(
        ...globalRoles.map((r) => r?.name ?? r?.roleName ?? r?.role).filter(Boolean)
      );
    }

    const unique = [...new Set(collected.map((r) => String(r).trim()).filter(Boolean))];
    const activeRoleNames = rowData
      .map((row) => row.roles)
      .filter((role) => role && role !== 'Select Role');
    return buildDsrTechnicianRoleOptions(unique, getDsrTechnicianAllowedRoles(globalRoles), activeRoleNames);
  }, [currentTechnicianLog, globalRoles, rowData]);

  useEffect(() => {
    onFetchRoles?.();
  }, [onFetchRoles]);

  const handleAddNewJobTitle = useCallback((rowId, name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return;

    const exists = roleOptions.some(
      (r) => r !== 'Select Role' && String(r).toLowerCase() === trimmed.toLowerCase()
    );
    if (!exists) {
      onAddRole?.({ name: trimmed });
    }

    updateRowField(rowId, 'roles', trimmed);
  }, [onAddRole, updateRowField, roleOptions]);

  const isCellDisabled = skipped || isSubmitted;

  const handleCommentsChange = (e) => {
    setComments(e.target.value);
  };

  const handleSkipChange = (e) => {
    const newSkipState = e.target.checked;
    setSkipped(newSkipState);

    if (onSkipChange) {
      onSkipChange(e);
    } else {
      if (currentDsrLog?.id) {
        const submissionStatus = generateSubmissionStatus('technician', shift, true);
        const sectionsSkipped = {
          ...currentDsrLog.sectionsSkipped,
          isTechnicianLogSkippedDayShift:
            shift === 'day'
              ? newSkipState
                ? 1
                : 0
              : currentDsrLog.sectionsSkipped?.isTechnicianLogSkippedDayShift || 0,
          isTechnicianLogSkippedNightShift:
            shift === 'night'
              ? newSkipState
                ? 1
                : 0
              : currentDsrLog.sectionsSkipped?.isTechnicianLogSkippedNightShift || 0,
        };

        onUpdateDsrLogSections({
          dsrLogId: currentDsrLog.id,
          submissionStatus: submissionStatus,
          sectionsSkipped: sectionsSkipped,
        });
      }
    }
  };

  const generateSubmissionStatus = (section, sh, submittedFlag = true) => {
    const shiftKeySuffix = sh === 'day' ? 'Dayshift' : 'Nightshift';
    let submittedKey;

    switch (section) {
      case 'technician':
        submittedKey = `isTechnicianLogSubmitted${shiftKeySuffix}`;
        break;
      case 'downtime':
        submittedKey = `isDowntimeSubmitted${shiftKeySuffix}`;
        break;
      case 'consumables':
        submittedKey = `isConsumablesSubmitted${shiftKeySuffix}`;
        break;
      case 'equipment':
        submittedKey = `isEquipmentUsageSubmitted${shiftKeySuffix}`;
        break;
      case 'ehs':
        submittedKey = `isEhsIssuesSubmitted${shiftKeySuffix}`;
        break;
      case 'material':
        submittedKey = `isMaterialUsageSubmitted${shiftKeySuffix}`;
        break;
      case 'production':
        submittedKey = `isProductionJointSubmitted${shiftKeySuffix}`;
        break;
      default:
        return {};
    }

    return { [submittedKey]: submittedFlag };
  };

  const handleSave = async () => {
    if (skipped) {
      if (onComplete) onComplete(true, 'technician');
      return;
    }

    try {
      await ensureDsrListingForNewRows();

      const dsrLogTechnicianMetadata = rowData.map((row) => {
        const statusString = mapUiStatusToApi(row.status);
        const roleString = (row.roles && row.roles !== 'Select Role') ? String(row.roles).toLowerCase() : '';
        const metadata = {
          hrs: row.hrs || 0,
          overTime: row.overTime || 0,
          userEmail: row.apiData?.userEmail,
          userFirstName: row.apiData?.userFirstName,
          userLastName: row.apiData?.userLastName,
          userProjectId: row.apiData?.userProjectId,
          status: statusString,
          role: roleString,
        };
        if (!isPendingTechnicianRowId(row.id)) {
          metadata.id = row.id;
        }
        return metadata;
      });

      const payload = {
        dsrLogTechnician: {
          id: currentTechnicianLog?.id,
          dsrLogId: currentDsrLog?.id,
          shift: getShiftNumber(shift),
          issues: comments,
          dsrLogTechnicianMetadata,
        },
      };

      await onUpdateTechnicianLog(payload);

      const submissionStatus = generateSubmissionStatus('technician', shift, true);
      const sectionsSkipped = {
        ...currentDsrLog.sectionsSkipped,
        isTechnicianLogSkippedDayShift:
          shift === 'day' ? 0 : currentDsrLog.sectionsSkipped?.isTechnicianLogSkippedDayShift || 0,
        isTechnicianLogSkippedNightShift:
          shift === 'night'
            ? 0
            : currentDsrLog.sectionsSkipped?.isTechnicianLogSkippedNightShift || 0,
      };

      await onUpdateDsrLogSections({
        dsrLogId: currentDsrLog?.id,
        submissionStatus: submissionStatus,
        sectionsSkipped: sectionsSkipped,
      });

      if (onComplete) onComplete(true, 'technician');

      if (selectedProject && selectedMainline && selectedDate) {
        const userEmail = JSON.parse(localStorage.getItem('user')).email;
        const logDate = moment(selectedDate).format('YYYY-MM-DD');
        const shiftNumber = getShiftNumber(shift);

        onFetchTechnicianLog({
          projectId: selectedProject.id,
          logDate: logDate,
          userEmail: userEmail,
          shift: shiftNumber,
          sowId: selectedMainline.id,
        });
      }
    } catch (error) {
      console.error('Error saving technician log:', error);
      notification.error({
        message: 'Save Failed',
        description: error?.message || 'Failed to save technician log data. Please try again.',
      });
    }
  };

  const deleteDisabled = selectedRowKeys.length === 0 || skipped || isShiftSkipped;

  return (
    <div className="modern-dsr-tech-log-section">
      {isShiftSkipped ? (
        <div className="modern-dsr-tech-log-skipped-banner">
          <strong>
            {shift.charAt(0).toUpperCase() + shift.slice(1)} Shift Skipped
          </strong>
          <p>
            This section is disabled because the {shift} shift has been skipped.
          </p>
        </div>
      ) : (
        <Spin
          spinning={isTechnicianLogLoading}
          indicator={(
            <span className="modern-dsr-tech-log-spin" aria-hidden>
              <Loader2 className="modern-dsr-tech-log-spin-icon" size={28} strokeWidth={2} />
            </span>
          )}
          tip="Loading technician log data..."
          size="large"
        >
          <div className="modern-dsr-tech-log-card">
            <div className="modern-dsr-tech-log-card-head modern-dsr-tech-head-with-toolbar">
              <div className="modern-dsr-tech-log-card-title-block">
                <div className="modern-dsr-tech-log-card-icon" aria-hidden>
                  <Users size={22} strokeWidth={2} />
                </div>
                <div>
                  <h3 className="modern-dsr-tech-log-card-title">Technician log</h3>
                  <p className="modern-dsr-tech-log-card-sub">Log team members, job title, status, and shift hours.</p>
                </div>
              </div>
              <div className="modern-dsr-tech-toolbar" role="group" aria-label="Technician row actions">
                <div className="modern-dsr-tech-toolbar-search">
                  <Search size={14} color="#9CA3AF" aria-hidden />
                  <input
                    type="search"
                    value={nameSearch}
                    onChange={(e) => setNameSearch(e.target.value)}
                    placeholder="Search by name…"
                    aria-label="Search technicians by name"
                    disabled={isCellDisabled}
                  />
                </div>
                <button
                  type="button"
                  className="modern-dsr-tech-icon-btn"
                  onClick={() => setIsAddModalVisible(true)}
                  disabled={isCellDisabled}
                  aria-label="Add technician"
                >
                  <Plus size={18} strokeWidth={2} />
                </button>
                <button
                  type="button"
                  className="modern-dsr-tech-icon-btn modern-dsr-tech-icon-btn--danger"
                  onClick={handleDeleteSelected}
                  disabled={deleteDisabled}
                  aria-label="Remove selected technicians"
                >
                  <Trash2 size={18} strokeWidth={2} />
                </button>
              </div>
            </div>

            <div className="modern-dsr-tech-table modern-dsr-tech-table--selectable" role="table" aria-label="Technician log">
              <div className="modern-dsr-tech-thead" role="row">
                <div className="modern-dsr-tech-th modern-dsr-tech-th--check" role="columnheader" aria-label="Select rows">
                  <Checkbox
                    checked={allInPageSelected}
                    indeterminate={someInPageSelected}
                    onChange={toggleSelectAllInPage}
                    disabled={pagedRows.length === 0 || isCellDisabled}
                  />
                </div>
                <div className="modern-dsr-tech-th" role="columnheader">Name</div>
                <div className="modern-dsr-tech-th modern-dsr-tech-th--center" role="columnheader">Job title</div>
                <div className="modern-dsr-tech-th modern-dsr-tech-th--center" role="columnheader">Status</div>
                <div className="modern-dsr-tech-th modern-dsr-tech-th--center" role="columnheader">Hrs</div>
                <div className="modern-dsr-tech-th modern-dsr-tech-th--center" role="columnheader">Over time</div>
              </div>

              {pagedRows.length === 0 ? (
                <div className="modern-dsr-tech-empty">
                  {nameSearch.trim()
                    ? 'No technicians match your search.'
                    : 'No technicians loaded.'}
                </div>
              ) : (
                pagedRows.map((row) => (
                  <div
                    key={row.id}
                    className="modern-dsr-tech-row"
                    role="row"
                    onMouseEnter={() => setHoveredRowId(row.id)}
                    onMouseLeave={() => setHoveredRowId(null)}
                    data-active={hoveredRowId === row.id ? 'true' : undefined}
                  >
                    <div className="modern-dsr-tech-td modern-dsr-tech-td--check" role="cell">
                      <Checkbox
                        checked={selectedRowKeys.includes(row.id)}
                        onChange={() => {
                          if (isCellDisabled) return;
                          setSelectedRowKeys((prev) => (
                            prev.includes(row.id)
                              ? prev.filter((k) => k !== row.id)
                              : [...prev, row.id]
                          ));
                        }}
                        disabled={isCellDisabled}
                        aria-label={`Select ${row.name}`}
                      />
                    </div>
                    <div className="modern-dsr-tech-td modern-dsr-tech-td--name" role="cell">
                      <span className="modern-dsr-tech-name-text" title={row.name}>
                        {row.name}
                      </span>
                    </div>
                    <div className="modern-dsr-tech-td" role="cell">
                      <TechnicianLogCustomSelect
                        id={`dd-role-${row.id}`}
                        value={row.roles}
                        options={roleOptions}
                        onChange={(v) => updateRowField(row.id, 'roles', v)}
                        disabled={isCellDisabled}
                        placeholder="Select Role"
                        showSearch
                        sortOptions
                      />
                    </div>
                    <div className="modern-dsr-tech-td" role="cell">
                      <TechnicianLogCustomSelect
                        id={`dd-status-${row.id}`}
                        value={row.status}
                        options={STATUS_SELECT_OPTIONS}
                        onChange={(v) => updateRowField(row.id, 'status', v)}
                        disabled={isCellDisabled}
                        placeholder="Select Status"
                        showSearch={false}
                      />
                    </div>
                    <div className="modern-dsr-tech-td modern-dsr-tech-td--num" role="cell">
                      <div className="modern-dsr-tech-hours-wrap">
                        <InputNumber
                          className="modern-dsr-tech-inputnum"
                          value={row.hrs}
                          min={0}
                          onChange={(v) => updateRowField(row.id, 'hrs', v == null ? 0 : v)}
                          disabled={isCellDisabled}
                        />
                      </div>
                    </div>
                    <div className="modern-dsr-tech-td modern-dsr-tech-td--num" role="cell">
                      <div className="modern-dsr-tech-hours-wrap">
                        <InputNumber
                          className="modern-dsr-tech-inputnum"
                          value={row.overTime}
                          min={0}
                          onChange={(v) => updateRowField(row.id, 'overTime', v == null ? 0 : v)}
                          disabled={isCellDisabled}
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="modern-dsr-tech-log-pagination-bar">
              <Pagination
                className="heat-profile-pagination modern-dsr-tech-heat-profile-pagination"
                current={page}
                pageSize={pageSize}
                total={totalRows}
                showSizeChanger
                pageSizeOptions={['10', '20', '50', '100']}
                showTotal={(t, range) =>
                  t > 0 ? `${range[0]}-${range[1]} of ${t}` : `0-0 of ${t}`
                }
                onChange={(p, newSize) => {
                  setPaginationState((s) => {
                    if (newSize !== s.pageSize) {
                      return { current: 1, pageSize: newSize };
                    }
                    return { ...s, current: p };
                  });
                }}
              />
            </div>

            <div className="modern-dsr-tech-log-issues-block">
              <div className="modern-dsr-tech-log-issues-label">Issues</div>
              <TextArea
                className="modern-dsr-tech-log-issues-textarea"
                rows={3}
                placeholder="Please add your issues here"
                value={comments}
                onChange={handleCommentsChange}
                disabled={skipped || isSubmitted}
              />
            </div>
          </div>
        </Spin>
      )}

      <AddTechnicianModal
        visible={isAddModalVisible}
        onCancel={() => setIsAddModalVisible(false)}
        onConfirm={handleAddTechnicians}
        teamMembers={projectTeamMembers}
        existingEmails={existingTechnicianEmails}
        loading={isAddingTechnicians}
      />

      <ConfirmationModal
        title="Remove technicians?"
        message={`Remove ${selectedRowKeys.length} selected technician${selectedRowKeys.length === 1 ? '' : 's'} from this shift log?`}
        confirmText="Remove"
        cancelText="Cancel"
        isOpen={isDeleteConfirmOpen}
        onConfirm={confirmDeleteSelected}
        onCancel={() => setIsDeleteConfirmOpen(false)}
        size="medium"
        confirmButtonProps={{
          style: {
            backgroundColor: '#EF4444',
            borderColor: '#EF4444',
          },
        }}
      />

      <div className="modern-dsr-tech-log-form-footer" aria-label="Form actions">
        <CrceButton label="Cancel" variant="tertiary" size="medium" onClick={onCancel} />
        <CrceButton
          label={isUpdatingTechnicianLog ? 'Saving…' : 'Save & Next'}
          variant="primary"
          size="medium"
          icon={Save}
          onClick={handleSave}
          disabled={isUpdatingTechnicianLog || isSubmitted || isShiftSkipped}
        />
      </div>
    </div>
  );
};

const mapStateToProps = ({ dsrReducer, rolesReduceer }) => ({
  isTechnicianLogLoading: dsrReducer.isTechnicianLogLoading,
  isUpdatingTechnicianLog: dsrReducer.isUpdatingTechnicianLog,
  currentTechnicianLog: dsrReducer.currentTechnicianLog,
  globalRoles: get(rolesReduceer, 'roles', []),
});

const mapDispatchToProps = (dispatch) => ({
  onFetchTechnicianLog: (...args) => dispatch(fetchTechnicianLog(...args)),
  onUpdateTechnicianLog: (...args) => dispatch(updateTechnicianLog(...args)),
  onUpdateDsrLogSections: (...args) => dispatch(updateDsrLogSections(...args)),
  onAddRole: (payload) => dispatch(addRole({ ...payload, personnelRoleType: 'coating' })),
  onFetchRoles: () => dispatch(fetchRoles()),
});

export default connect(mapStateToProps, mapDispatchToProps)(TechnicianLogSection);
